import type { ExtendedOrganization } from '@/hooks/queries/useOrganizationsQuery'
import type { useOrganizationStore } from '@/store/organization.store'

import type { ExternalOrgRelationship } from '@scrambl/shared'

type Selection = ReturnType<typeof useOrganizationStore.getState>
type RouteAccess = {
  organization: NonNullable<Selection['currentOrganization']>
  relationship: ExternalOrgRelationship | null
} | null

/** Refresh the selected path; never replace a revoked grant with another path. */
export function resolveOrganizationRouteAccess(
  slug: string,
  current: Selection['currentOrganization'],
  selectedRelationship: Selection['externalOrgRelationship'],
  organizations: ExtendedOrganization[],
  relationships: ExternalOrgRelationship[],
): RouteAccess {
  // Slugs can change while the stable workspace identity and selected grant
  // stay the same. A rename must not implicitly choose direct membership.
  const selected =
    !!current &&
    (current.slug === slug ||
      organizations.some(org => org.id === current.id && org.slug === slug) ||
      relationships.some(
        rel =>
          rel.hostOrganizationId === current.id &&
          rel.hostOrganization?.id === current.id &&
          rel.hostOrganization.slug === slug,
      ))
  const direct = organizations.find(
    org => org.slug === slug && (!selected || org.id === current.id),
  )
  const activeDirect = direct?.userMembership?.status === 'ACTIVE'
  if ((!selected || !current.isExternalAccess) && activeDirect) {
    return {
      organization: {
        ...direct,
        ...(selected ? current : {}),
        id: direct.id,
        slug: direct.slug,
        name: direct.name,
        accountType: direct.accountType,
        userMembership: direct.userMembership,
        isExternalAccess: false,
      },
      relationship: null,
    }
  }
  if (selected && (!current.isExternalAccess || !selectedRelationship?.id))
    return null
  const matches = relationships.filter(
    rel =>
      rel.status === 'ACTIVE' &&
      !rel.revokedAt &&
      rel.hostOrganization?.slug === slug &&
      rel.hostOrganization.id === rel.hostOrganizationId &&
      (!selected ||
        (rel.hostOrganizationId === current.id &&
          rel.id === selectedRelationship?.id)),
  )
  if (matches.length !== 1) return null
  const relationship = matches[0]
  const host = relationship.hostOrganization!
  return {
    organization: {
      ...(selected ? current : {}),
      id: host.id,
      name: host.name,
      slug: host.slug,
      logo: host.logo ?? null,
      userMembership: null,
      isExternalAccess: true,
    } as NonNullable<Selection['currentOrganization']>,
    relationship,
  }
}
